import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
// tslint:disable-next-line:nx-enforce-module-boundaries
import {DropDownOption} from "../../../../../../shared/ui/src/lib/shared/elements/drop-down/drop-down.component";
import {ContractPartnersFacade} from "../../../../../../shared/data-access/data-access-contract-partners/src";
import {BehaviorSubject, Observable, Subject} from "rxjs";
import {map, tap} from 'rxjs/operators';
import {SubSink} from "subsink";
import {BaseSite, ContractPartner, FulfillmentByEntry} from "@kion/data-models";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
// tslint:disable-next-line:nx-enforce-module-boundaries
import {CmsConfig} from "../../../../../cms/src/lib/config/cms.config";
import {MarkdownService} from "ngx-markdown";
import {BaseSitesFacade} from "@kion/data-access/data-access-base-sites";
import {BasketsFacade} from "@kion/data-access/data-access-baskets";
import {CorrelationObserverService} from "@kion/ngrx-helper";
// tslint:disable-next-line:nx-enforce-module-boundaries
import {ToastService} from "@kion/ui";


@Component({
  selector: 'kion-network-partner-choose-dialog',
  templateUrl: './network-partner-choose-dialog.component.html',
  styleUrls: ['./network-partner-choose-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NetworkPartnerChooseDialogComponent implements OnInit {

  data$: Observable<SafeHtml>;
  baseSite$: Observable<BaseSite>;
  showConfirm$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  networkPartners$: Observable<DropDownOption[]> = this.getNetworkPartners$();
  selectedNetworkPartner$: Observable<ContractPartner> = new Subject<ContractPartner>();
  submitButtonActive$: Observable<boolean> = new BehaviorSubject<boolean>(false);
  currentSelectedNetworkPartnerId$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  title$: BehaviorSubject<string> = new BehaviorSubject<string>("");
  termsAndConditionLink$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  privacyLink$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  project: string;
  currentFulfillmentPartner: FulfillmentByEntry;

  private subSink: SubSink;

  constructor(private dialog: NgbActiveModal,
              private contractPartnersFacade: ContractPartnersFacade,
              private cmsConfig: CmsConfig,
              private markdownService: MarkdownService,
              private sanitizer: DomSanitizer,
              private baseSiteFacade: BaseSitesFacade,
              private basketsFacade: BasketsFacade,
              private observer: CorrelationObserverService,
              private toastService: ToastService
  ) {
    this.project = this.cmsConfig.project;
    this.subSink = new SubSink();
    this.baseSite$ = baseSiteFacade.all$.pipe(
      map(sites => sites.find(site => site.uid === this.cmsConfig.baseSite))
    );

  }

  get submitButtonActive(): Observable<boolean> {
    return this.submitButtonActive$;
  }

  private static formatName(contractPartner) {
    return `${contractPartner.registeredName} (${contractPartner.address?.postalCode}, ${contractPartner.address?.town})`;
  }

  ngOnInit() {
    this.observer.subscribeToAction(
      this.contractPartnersFacade.loadAll(),
      () => {
        this.currentSelectedNetworkPartnerId$.next(this.currentFulfillmentPartner.uid);
      },
      () => {
        this.toastService.show(
          {
            text: 'could not load partners',
            type: 'error'
          }
        );
      }
    );
  }

  closeDialog() {
    this.dialog.close({result: "just closed"});
  }

  updateSelection($event: DropDownOption) {
    this.networkPartners$ = this.networkPartners$.pipe(
      map(
        elements => elements.map(
          element => {
            if (element.key === $event.key) {
              this.title$.next($event.value);
              this.updateSelectedNetworkPartner($event.key);
            }
            return {
              ...element,
              selected: element.key === $event.key
            }
          }
        )
      )
    );

    this.updateSubmitButtonActive();
  }

  selectCurrentNetworkPartner() {
    this.subSink.sink = this.selectedNetworkPartner$.subscribe(value => {
      this.observer.subscribeToAction(
        this.contractPartnersFacade.setContractPartner(value.id, window.localStorage.getItem('basketId')),
        () => {
          this.basketsFacade.createOrFindBasket();
          this.currentSelectedNetworkPartnerId$.next(value.id);
          this.showConfirm$.next(true);
        },
        () => {
          this.toastService.show(
            {
              text: 'could not select partner',
              type: 'error'
            }
          );
        }
      )

    })
  }

  closeConfirmDialog() {
    this.updateSubmitButtonActive();
    this.showConfirm$.next(false);
  }

  private updateSubmitButtonActive() {
    this.submitButtonActive$ = this.networkPartners$.pipe(
      map(partners => partners.find(partner => partner.selected)),
      map(selectedPartner => this.currentSelectedNetworkPartnerId$.value !== selectedPartner.key)
    );
  }

  private sortByShortName = (a: ContractPartner, b: ContractPartner) => {
    if (a.registeredName < b.registeredName)
      return -1;
    if (a.registeredName > b.registeredName)
      return 1;
    return 0;
  }

  private updateSelectedNetworkPartner(id: string): void {
    this.selectedNetworkPartner$ = this.contractPartnersFacade.all$.pipe(
      map(contractPartners => contractPartners.find(contractPartner => contractPartner.uid === id))
    );
    this.data$ = this.selectedNetworkPartner$.pipe(
      map((contractPartner) => {
          this.termsAndConditionLink$.next(`${this.cmsConfig.routes.partnerBaseRoute}/${contractPartner.id}/${this.cmsConfig.routes.termsBaseRoute}`);
          this.privacyLink$.next(`${this.cmsConfig.routes.partnerBaseRoute}/${contractPartner.id}/${this.cmsConfig.routes.privacyPolicyBaseRoute}`);
          return this.sanitizer.bypassSecurityTrustHtml(this.markdownService.compile(contractPartner.legalNotice))
        }
      ));
  }

  private getNetworkPartners$() {
    return this.contractPartnersFacade.all$.pipe(
      tap(contractPartners => contractPartners.sort(this.sortByShortName)),
      map(contractPartners => contractPartners.map(
        contractPartner => {
          if (contractPartner.uid === this.currentFulfillmentPartner?.uid) {
            this.title$.next(NetworkPartnerChooseDialogComponent.formatName(contractPartner));
            this.updateSelectedNetworkPartner(contractPartner.uid);
          }
          return {
            key: contractPartner.id,
            value: NetworkPartnerChooseDialogComponent.formatName(contractPartner),
            selected: contractPartner.uid === this.currentFulfillmentPartner?.uid
          } as DropDownOption
        }
      ))
    );
  }
}
